<script setup>
import {computed, ref, watch} from 'vue'
import {useRouter} from 'vue-router'
import Button from "@/common/Button.vue"
import api from '@/services/api'
import {usePageEditorStore} from '@/stores/pageEditor'
import {useTemplatesStore} from '@/stores/templates'
import {useItemDataStore} from '@/stores/itemData'

const router = useRouter()
const pageEditorStore = usePageEditorStore()
const templatesStore = useTemplatesStore()
const itemDataStore = useItemDataStore()

const closeBtn = ref(null)

const activePageContentId = computed(() => pageEditorStore.activePageContentId)
const activeContent = computed(() => pageEditorStore.contentItems.find(o => o.pageContentId === activePageContentId.value))
const contentAreaName = computed(() => pageEditorStore.activeContentAreaName || activeContent.value?.contentAreaName)
const contentArea = computed(() => pageEditorStore.contentAreas.find(o => o.name === contentAreaName.value))
const contentTypes = computed(() => templatesStore.contentTypes || [])
const groups = computed(() => [...new Set(contentTypes.value.filter(o => o.pageEditorGroup).map(o => o.pageEditorGroup))].sort())
const pageId = computed(() => pageEditorStore.pageId)
const offcanvasEnd = computed(() => pageEditorStore.offcanvasEnd)

const page = ref(undefined)

watch(
    pageId,
    async (newPageId) => {
        if (!newPageId) {
            page.value = undefined
            return
        }
        page.value = await itemDataStore.get({
            tableName: 'pages',
            id: newPageId
        })
    },
    {immediate: true}
)

const getTypes = (group) => {
    let types = contentTypes.value

    if (page.value) {
        if (group === 'Listing') {
            types = types.filter(o => page.value.componentId === o.componentId)
        } else if (group === 'Listing component') {
            types = page.value.type === 'Listing'
                ? types.filter(o => page.value.componentId === o.componentId || o.componentId === 0)
                : []
        } else if (group === 'Product filter') {
            // Todo remove product filters unless this is the shop listing
        }
    }

    return types.filter(o => o.pageEditorGroup === group)
}

const addContent = async (contentTypeId) => {
    try {
        const o = await api.post('api/component/content', {contentType: contentTypeId})
        const contentId = o.data.id

        const pageContentId = await pageEditorStore.addContentToPage({
            contentId: contentId,
            pageSectionLinkId: contentArea.value.sectionId,
            contentAreaName: contentAreaName.value
        })

        await pageEditorStore.goToContent({
            router: router,
            pageContentId: pageContentId
        })

        closeBtn.value?.click()
    } catch (err) {
        // Handle error if needed
    }
}
</script>

<template>
    <div ref="offcanvas" id="addContentOffcanvas" aria-labelledby="addContentOffcanvasLabel"
         class="offcanvas" :class="{'offcanvas-start': !offcanvasEnd, 'offcanvas-end': offcanvasEnd}" tabindex="-1">
        <div class="offcanvas-header">
            <button ref="closeBtn" type="button" class="btn-close" data-bs-dismiss="offcanvas"
                    aria-label="Close"></button>
        </div>

        <div class="offcanvas-body">
            <div class="accordion small border-0" id="accordionExample">
                <template v-for="(group, key) in groups" :key="key">
                    <div v-if="getTypes(group).length" class="accordion-item border-0 rounded overflow-hidden">
                        <h4 class="accordion-header" :id="group">
                            <button class="accordion-button collapsed p-2" type="button" data-bs-toggle="collapse"
                                    :data-bs-target="'#collapse' + key" aria-expanded="false"
                                    :aria-controls="'collapse' + key">
                                {{ group }}
                            </button>
                        </h4>

                        <div :id="'collapse' + key" class="accordion-collapse collapse"
                             data-bs-parent="#accordionExample">
                            <div class="accordion-body p-0 pb-4">
                                <div class="d-grid gap-1">
                                    <Button v-for="contentType in getTypes(group)" :key="contentType.id"
                                            @click="addContent(contentType.id)"
                                            class="btn-sm border d-flex align-items-center gap-2">
                                        {{ contentType.title }}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped>
.accordion-button:not(.collapsed) {
    background-color: transparent !important;
    color: inherit;
}

button.accordion-button:focus {
    box-shadow: inherit;
}
</style>